import { useCallback, useEffect, useRef, useState } from 'react'

import { useSessionStore } from '@/lib/auth/session-store'
import { useRealtimeManager } from '@/lib/realtime'

import {
  chatApi,
  type PrivateConversation,
  type PrivateMessage,
} from '../api'
import {
  isPrivateMessage,
  mergePrivateMessage,
  mergePrivateTimeline,
  privateDisplayName,
} from '../format'
import type { RealtimeTypingEvent } from './use-chat-realtime'
import { useChatRealtime } from './use-chat-realtime'

export type PrivateChatPhase = 'loading' | 'ready' | 'error'

export interface PrivatePeer {
  userId: string | null
  name: string | null
  avatarUrl: string | null
  pinned: boolean
}

export interface PrivateChatState {
  conversation: PrivateConversation | null
  peer: PrivatePeer
  messages: PrivateMessage[]
  phase: PrivateChatPhase
  refreshing: boolean
  error: unknown
  /** Ephemeral peer typing hint; never persisted (REALTIME.md §9). */
  typing: boolean
  refresh: () => void
  setMessages: (updater: (items: PrivateMessage[]) => PrivateMessage[]) => void
  setPinned: (pinned: boolean) => void
  notifyTyping: (typing: boolean) => void
  markRead: (messageId: string) => Promise<void>
}

const EMPTY_PEER: PrivatePeer = {
  userId: null,
  name: null,
  avatarUrl: null,
  pinned: false,
}

function derivePeer(
  conversation: PrivateConversation | null,
  messages: PrivateMessage[],
): PrivatePeer {
  if (conversation) {
    return {
      userId: conversation.userId,
      name: privateDisplayName(conversation),
      avatarUrl: conversation.avatarUrl,
      pinned: conversation.pinned,
    }
  }
  // Hidden conversations are omitted from the list. A notification deep link
  // still renders, deriving the peer from the latest incoming message.
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message && !message.isMine) {
      return {
        userId: message.senderId,
        name: message.publicName || message.nickname,
        avatarUrl: message.avatarUrl,
        pinned: false,
      }
    }
  }
  return EMPTY_PEER
}

/**
 * Private conversation route state. Conversation metadata comes from the
 * frozen list endpoint (the message list does not include the peer id); a
 * direct link to a hidden conversation derives the peer from messages instead
 * of failing.
 */
export function usePrivateChat(
  conversationId: string | null,
): PrivateChatState {
  const realtime = useRealtimeManager()
  const [conversation, setConversation] = useState<PrivateConversation | null>(
    null,
  )
  const [peer, setPeer] = useState<PrivatePeer>(EMPTY_PEER)
  const [messages, setMessagesState] = useState<PrivateMessage[]>([])
  const [phase, setPhase] = useState<PrivateChatPhase>('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [typingState, setTypingState] = useState<{
    conversationId: string | null
    value: boolean
  }>({ conversationId: null, value: false })
  const [request, setRequest] = useState(0)
  const loadedRef = useRef(false)
  const inFlightRef = useRef(false)
  const lastReadRef = useRef<string | null>(null)
  const readInFlightRef = useRef(false)
  const mutationRef = useRef(0)
  const conversationRef = useRef<PrivateConversation | null>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const typingSentRef = useRef({ value: false, at: 0 })

  useEffect(() => {
    conversationRef.current = conversation
  }, [conversation])

  const clearTypingTimer = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    lastReadRef.current = null
    typingSentRef.current = { value: false, at: 0 }
    clearTypingTimer()
  }, [conversationId, clearTypingTimer])

  useEffect(() => {
    return () => {
      clearTypingTimer()
    }
  }, [clearTypingTimer])

  useEffect(() => {
    if (!conversationId) return
    const controller = new AbortController()
    const mutationAtStart = mutationRef.current
    inFlightRef.current = true
    if (!loadedRef.current) setPhase('loading')

    Promise.allSettled([
      chatApi.listPrivateConversations({ signal: controller.signal }),
      chatApi.listPrivateMessages(conversationId, {
        signal: controller.signal,
      }),
    ])
      .then(([conversationsResult, messagesResult]) => {
        if (controller.signal.aborted) return
        const messagesResponse =
          messagesResult.status === 'fulfilled' ? messagesResult.value : null
        if (!messagesResponse) {
          setError(
            messagesResult.status === 'rejected'
              ? messagesResult.reason
              : new Error('Private chat unavailable'),
          )
          setRefreshing(false)
          if (!loadedRef.current) setPhase('error')
          return
        }

        const list =
          conversationsResult.status === 'fulfilled'
            ? conversationsResult.value
            : null
        const nextConversation =
          list?.items.find((item) => item.id === conversationId) ?? null

        setConversation(nextConversation)
        if (mutationRef.current === mutationAtStart) {
          setPeer(derivePeer(nextConversation, messagesResponse.items))
          setMessagesState(messagesResponse.items)
        } else {
          setMessagesState((current) =>
            mergePrivateTimeline(messagesResponse.items, current),
          )
          if (!nextConversation) {
            setPeer((current) =>
              current.userId
                ? current
                : derivePeer(null, messagesResponse.items),
            )
          }
        }
        loadedRef.current = true
        setPhase('ready')
        setRefreshing(false)
        setError(null)
        inFlightRef.current = false
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setRefreshing(false)
        inFlightRef.current = false
        if (!loadedRef.current) setPhase('error')
      })

    return () => controller.abort()
  }, [conversationId, request])

  const refresh = useCallback(() => {
    setError(null)
    if (loadedRef.current) setRefreshing(true)
    setRequest((value) => value + 1)
  }, [])

  const sync = useCallback(() => {
    if (inFlightRef.current) return
    setRequest((value) => value + 1)
  }, [])

  const appendRealtimeMessage = useCallback(
    (_conversationId: string, message: unknown): boolean => {
      if (!isPrivateMessage(message)) return false
      mutationRef.current += 1
      setMessagesState((current) => mergePrivateMessage(current, message))
      if (!conversationRef.current && !message.isMine) {
        setPeer((current) =>
          current.userId ? current : derivePeer(null, [message]),
        )
      }
      return true
    },
    [],
  )

  const handleTyping = useCallback(
    (event: RealtimeTypingEvent) => {
      if (event.userId === useSessionStore.getState().user?.id) return
      clearTypingTimer()
      if (!event.typing) {
        setTypingState({ conversationId: event.conversationId, value: false })
        return
      }
      setTypingState({ conversationId: event.conversationId, value: true })
      typingTimerRef.current = setTimeout(() => {
        typingTimerRef.current = null
        setTypingState({ conversationId: event.conversationId, value: false })
      }, 3_000)
    },
    [clearTypingTimer],
  )

  useChatRealtime({
    channel: 'conversation',
    id: conversationId,
    onUpdate: sync,
    onPrivateMessage: appendRealtimeMessage,
    onTyping: handleTyping,
  })

  const notifyTyping = useCallback(
    (value: boolean) => {
      if (!conversationId) return
      const now = Date.now()
      const previous = typingSentRef.current
      if (value) {
        if (previous.value && now - previous.at < 3_000) return
      } else if (!previous.value) {
        return
      }
      typingSentRef.current = { value, at: now }
      realtime.sendTyping(conversationId, value)
    },
    [conversationId, realtime],
  )

  const markRead = useCallback(
    async (messageId: string) => {
      if (!conversationId || !messageId) return
      if (lastReadRef.current === messageId || readInFlightRef.current) return
      readInFlightRef.current = true
      lastReadRef.current = messageId
      try {
        await chatApi.readPrivateMessages(conversationId, messageId)
      } catch {
        if (lastReadRef.current === messageId) lastReadRef.current = null
      } finally {
        readInFlightRef.current = false
      }
    },
    [conversationId],
  )

  const setMessages = useCallback(
    (updater: (items: PrivateMessage[]) => PrivateMessage[]) => {
      mutationRef.current += 1
      setMessagesState(updater)
    },
    [],
  )

  const setPinned = useCallback((pinned: boolean) => {
    setPeer((current) => ({ ...current, pinned }))
    setConversation((current) =>
      current ? { ...current, pinned } : current,
    )
  }, [])

  return {
    conversation,
    peer,
    messages,
    phase,
    refreshing,
    error,
    typing:
      typingState.value && typingState.conversationId === conversationId,
    refresh,
    setMessages,
    setPinned,
    notifyTyping,
    markRead,
  }
}
